import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Shell, TopBar } from "../components/Shell";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { api } from "../lib/api";

export default function Intake() {
  const navigate = useNavigate();
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("");
  const [complaint, setComplaint] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const a = parseInt(age, 10);
    if (!a || a < 1 || a > 120) return toast.error("Please enter a valid age (1–120).");
    if (!sex) return toast.error("Please select your sex.");
    if (!complaint.trim()) return toast.error("Please describe your main complaint.");
    setSaving(true);
    try {
      const r = await api.post("/screenings", {
        patient: {
          age: a, sex, chief_complaint: complaint.trim(),
          name: name || null, phone: phone || null, email: email || null,
        },
      });
      navigate(`/complaint?sid=${r.data.id}`);
    } catch (e) {
      toast.error("Could not start. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell header={<TopBar title="About you" onBack={() => navigate("/")} />}>
      <div className="px-6 py-5 flex flex-col gap-5 pb-28">
        <p className="text-sm text-muted-foreground">
          A few quick details help us tailor your screening. Fields marked * are required.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="age">Age *</Label>
            <Input id="age" data-testid="intake-age" type="number" inputMode="numeric" min="1" max="120"
              placeholder="e.g. 32" value={age} onChange={(e) => setAge(e.target.value)} className="h-12 rounded-xl" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Sex *</Label>
            <Select value={sex} onValueChange={setSex}>
              <SelectTrigger data-testid="intake-sex" className="h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="other">Other</SelectItem>
                <SelectItem value="prefer_not">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="complaint">Main complaint *</Label>
          <Textarea id="complaint" data-testid="intake-complaint"
            placeholder="e.g. Lower right tooth hurts with cold water; gums bleed when brushing."
            value={complaint} onChange={(e) => setComplaint(e.target.value)}
            className="min-h-28 rounded-xl resize-none" />
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium">Optional contact details</p>
          <Input data-testid="intake-name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-xl" />
          <Input data-testid="intake-phone" placeholder="Phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 rounded-xl" />
          <Input data-testid="intake-email" placeholder="Email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl" />
        </div>
      </div>

      <div className="sticky bottom-0 px-6 py-4 bg-background border-t border-border">
        <button data-testid="intake-continue" onClick={submit} disabled={saving}
          className="tap w-full h-14 rounded-2xl brand-gradient text-white font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Continue <ArrowRight className="w-5 h-5" /></>)}
        </button>
      </div>
    </Shell>
  );
}
